import axios, { AxiosResponse } from 'axios'
import { FilterDTO } from 'medere-ui-framework'
import { axiosInstance } from '../App'
import { LoadConfigDTO, SortingFieldDTO } from '../dto/LoadConfigDTO'
import {
  OperatingRoomCancellationsDTO,
  OperatingRoomCriteriaDTO,
  OperatingRoomDayDTO,
  OperatingRoomEvolutionDTO,
  OperatingRoomExportFormat,
  OperatingRoomFilterDTO,
  OperatingRoomGranularity,
  OperatingRoomGroupBy,
  OperatingRoomGroupsDTO,
  OperatingRoomHeatCellDTO,
  OperatingRoomHeatMeasure,
  OperatingRoomHeatmapDTO,
  OperatingRoomNomenclatorDTO,
  OperatingRoomOptionsDTO,
  OperatingRoomOutcome,
  OperatingRoomRoomsDTO,
  OperatingRoomRowDTO,
  OperatingRoomRowsPageDTO,
  OperatingRoomSummaryDTO,
  OperatingRoomTimesDTO,
} from '../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { ApiErrorResponse, parseApiError } from './apiError'

/** Prefijo de los endpoints securizados: se define una sola vez para esta capa de servicios. */
export const SECURED_API_BASE = '/api/secured'
const OPERATING_ROOM_API = `${SECURED_API_BASE}/operating-room-indicators`

/** Códigos de error del backend que la pantalla distingue. */
export const OPERATING_ROOM_ACCESS_DENIED = 'ACCESS_DENIED'
/** Se pidió agrupar por cirujano sin la acción 46. */
export const OPERATING_ROOM_SURGEON_GROUPING_DENIED = 'SURGEON_GROUPING_DENIED'
/** Se pidió un dato del cirujano (el filtro, la columna) sin la acción 46. */
export const OPERATING_ROOM_SURGEON_ACCESS_DENIED = 'SURGEON_ACCESS_DENIED'

/**
 * Lo que `RemoteTable` le pasa a `getItems` y a la exportación: sus filtros de columna y su orden, más lo que la pantalla le da
 * en `params` (no es de columna: viaja aparte). `outcome` y `cell` son de la lista: qué cirugías muestra y, si vino de una celda
 * del mapa de calor, cuál.
 */
export interface OperatingRoomTableParams {
  filters?: OperatingRoomFilterDTO
  outcome?: OperatingRoomOutcome
  cell?: OperatingRoomHeatCellDTO
  measure?: OperatingRoomHeatMeasure
  filterDTO?: FilterDTO | null
  multiSortMeta?: { fieldName: string; ascOrder: boolean }[]
}

/** Error del backend ya traducido al contrato `ErrorResponse`: el componente nunca ve el error crudo de axios. */
export class OperatingRoomApiError extends Error {
  readonly status: number
  readonly errorCode: string

  constructor(apiError: ApiErrorResponse) {
    super(apiError.message)
    this.name = 'OperatingRoomApiError'
    this.status = apiError.status
    this.errorCode = apiError.errorCode
  }
}

/** Con `responseType: 'blob'` el cuerpo del error también llega como Blob: se lee para que `parseApiError` vea el JSON. */
async function unwrapBlobError(error: unknown): Promise<void> {
  if (!axios.isAxiosError(error) || !error.response || !(error.response.data instanceof Blob)) return
  try {
    error.response.data = JSON.parse(await error.response.data.text()) as unknown
  } catch {
    error.response.data = null
  }
}

/** Único punto donde se parsea el error de axios (contrato `ErrorResponse`). */
async function toOperatingRoomApiError(error: unknown): Promise<OperatingRoomApiError> {
  await unwrapBlobError(error)
  return new OperatingRoomApiError(parseApiError(error))
}

/** True si el pedido se canceló con el `AbortController`: no es un error para mostrar. */
export function isRequestCanceled(error: unknown): boolean {
  return axios.isCancel(error)
}

/** True si el backend respondió que el perfil no tiene acceso a lo pedido: a la pantalla (403 `ACCESS_DENIED`) o a los datos del cirujano (403 `SURGEON_ACCESS_DENIED`). */
export function isAccessDenied(error: unknown): boolean {
  return error instanceof OperatingRoomApiError
    && (error.errorCode === OPERATING_ROOM_ACCESS_DENIED || error.errorCode === OPERATING_ROOM_SURGEON_ACCESS_DENIED)
}

/** True si lo que se rechazó son los datos del cirujano (403 `SURGEON_ACCESS_DENIED`): la pantalla sí es accesible. */
export function isSurgeonAccessDenied(error: unknown): boolean {
  return error instanceof OperatingRoomApiError && error.errorCode === OPERATING_ROOM_SURGEON_ACCESS_DENIED
}

/** True si el perfil no puede agrupar por cirujano (403 `SURGEON_GROUPING_DENIED`). */
export function isSurgeonGroupingDenied(error: unknown): boolean {
  return error instanceof OperatingRoomApiError && error.errorCode === OPERATING_ROOM_SURGEON_GROUPING_DENIED
}

/**
 * Mensaje para mostrar: el del backend si es un error de negocio (4xx: rango inválido, sin permiso), que ya viene
 * filtrado y dice qué hacer; si no (red, servidor), el texto genérico de la acción que falló.
 */
export function errorMessageOf(error: unknown, fallback: string): string {
  return error instanceof OperatingRoomApiError && error.status >= 400 && error.status < 500 ? error.message : fallback
}

async function request<TResponse>(
  path: string,
  body: unknown,
  signal?: AbortSignal,
  responseType?: 'blob',
): Promise<AxiosResponse<TResponse>> {
  try {
    return await axiosInstance.post<TResponse>(`${OPERATING_ROOM_API}${path}`, body, { signal, responseType })
  } catch (error: unknown) {
    if (axios.isCancel(error)) throw error
    throw await toOperatingRoomApiError(error)
  }
}

async function post<TResponse>(path: string, body: unknown, signal?: AbortSignal): Promise<TResponse> {
  return (await request<TResponse>(path, body, signal)).data
}

/** Paginación, orden y filtros de columna de `RemoteTable` en el formato del backend (`LoadConfigDTO`). Sin página para el export. */
function buildLoadConfig(offSet: number | null, pageSize: number | null, params?: OperatingRoomTableParams): LoadConfigDTO {
  const sorting: SortingFieldDTO[] = (params?.multiSortMeta ?? []).map((sort) => ({
    fieldName: sort.fieldName,
    ascOrder: sort.ascOrder,
    sqlFormula: null,
    compositeSortOrder: null,
  }))
  const filterDTO = params?.filterDTO
  const filters = filterDTO && filterDTO.filters.length > 0
    ? {
      associationType: filterDTO.associationType,
      filters: filterDTO.filters.map((filter) => ({
        fieldName: filter.fieldName,
        filterType: filter.filterType,
        sqlType: filter.sqlType ?? null,
        values: filter.values,
        sql: null,
      })),
      filterLists: [],
    }
    : null
  return { offSet, pageSize, sortingFieldInfoList: sorting, filters }
}

/** El cuerpo de la lista: sin filtros de pantalla el backend rechaza el pedido, así que acá son obligatorios. */
function listCriteria(
  offSet: number | null,
  pageSize: number | null,
  params: OperatingRoomTableParams,
): OperatingRoomCriteriaDTO {
  if (!params.filters) throw new Error('Faltan los filtros de la pantalla')
  const criteria: OperatingRoomCriteriaDTO = { filters: params.filters, loadConfigDTO: buildLoadConfig(offSet, pageSize, params) }
  if (params.outcome) criteria.outcome = params.outcome
  if (params.cell) {
    criteria.cell = params.cell
    criteria.measure = params.measure ?? 'HOURS'
  }
  return criteria
}

/** Opciones de los combos de «Más filtros» y permiso de agrupar por cirujano. */
export function getOperatingRoomOptions(signal?: AbortSignal): Promise<OperatingRoomOptionsDTO> {
  return post<OperatingRoomOptionsDTO>('/options', {}, signal)
}

/** Búsqueda remota de códigos de nomenclador de las cirugías del sitio (mínimo 2 caracteres). */
export function searchOperatingRoomNomenclators(search: string, signal?: AbortSignal): Promise<OperatingRoomNomenclatorDTO[]> {
  return post<OperatingRoomNomenclatorDTO[]>('/nomenclators', { search, limit: 20 }, signal)
}

/** Indicadores del período y del anterior: una vez por cambio de filtros de pantalla. */
export function getOperatingRoomSummary(filters: OperatingRoomFilterDTO, signal?: AbortSignal): Promise<OperatingRoomSummaryDTO> {
  return post<OperatingRoomSummaryDTO>('/summary', { filters }, signal)
}

/** Un punto por día (o por semana si el período pasa de 90 días). */
export function getOperatingRoomEvolution(
  filters: OperatingRoomFilterDTO,
  granularity: OperatingRoomGranularity,
  signal?: AbortSignal,
): Promise<OperatingRoomEvolutionDTO> {
  return post<OperatingRoomEvolutionDTO>('/evolution', { filters, granularity }, signal)
}

/** Las salas de un día (o tramo) con sus cirugías y minutos de sala por hora: el panel del día. */
export function getOperatingRoomDay(filters: OperatingRoomFilterDTO, signal?: AbortSignal): Promise<OperatingRoomDayDTO> {
  return post<OperatingRoomDayDTO>('/day', { filters }, signal)
}

/** Matriz de 7 días por 24 horas con la medida elegida. */
export function getOperatingRoomHeatmap(
  filters: OperatingRoomFilterDTO,
  measure: OperatingRoomHeatMeasure,
  signal?: AbortSignal,
): Promise<OperatingRoomHeatmapDTO> {
  return post<OperatingRoomHeatmapDTO>('/heatmap', { filters, measure }, signal)
}

/** Una fila por quirófano, con el total del período. */
export function getOperatingRoomRooms(filters: OperatingRoomFilterDTO, signal?: AbortSignal): Promise<OperatingRoomRoomsDTO> {
  return post<OperatingRoomRoomsDTO>('/rooms', { filters }, signal)
}

/** Tiempos de las cirugías realizadas (puntualidad, espera, duración, recambio), con los del período anterior y por tipo y por práctica. */
export function getOperatingRoomTimes(filters: OperatingRoomFilterDTO, signal?: AbortSignal): Promise<OperatingRoomTimesDTO> {
  return post<OperatingRoomTimesDTO>('/times', { filters }, signal)
}

/** Anuladas, reprogramadas y ausentes (tres cuentas) con su anticipación y los del período anterior, por sala y por tipo. */
export function getOperatingRoomCancellations(filters: OperatingRoomFilterDTO, signal?: AbortSignal): Promise<OperatingRoomCancellationsDTO> {
  return post<OperatingRoomCancellationsDTO>('/cancellations', { filters }, signal)
}

/** Las cirugías realizadas agrupadas por tipo, código, cirujano, anestesia, financiador o agenda (tabla completa en memoria). */
export function getOperatingRoomGroups(
  filters: OperatingRoomFilterDTO,
  groupBy: OperatingRoomGroupBy,
  signal?: AbortSignal,
): Promise<OperatingRoomGroupsDTO> {
  return post<OperatingRoomGroupsDTO>('/groups', { filters, groupBy }, signal)
}

/** Una página de la lista (scroll infinito), con los filtros de pantalla y los de columna. El total no se muestra: se descarta. */
export async function getOperatingRoomRows(
  offSet: number,
  pageSize: number,
  params: OperatingRoomTableParams,
): Promise<OperatingRoomRowDTO[]> {
  return (await post<OperatingRoomRowsPageDTO>('/list', listCriteria(offSet, pageSize, params))).rows
}

/**
 * Exporta una pestaña que no es la lista (`SUMMARY`/`EVOLUTION`/`HEATMAP`/`ROOMS`/`GROUPS`/`TIMES`/`CANCELLATIONS`) en PDF o Excel con los filtros de la
 * pantalla. Devuelve la respuesta completa: `ExportMenu` baja el archivo con el nombre del `Content-Disposition`. El error se
 * deja sin traducir: `ExportMenu` lo lee (el cuerpo del error también llega como Blob) y muestra el mensaje del backend si es de
 * negocio.
 */
export function exportOperatingRoomTab(
  scope: 'SUMMARY' | 'EVOLUTION' | 'HEATMAP' | 'ROOMS' | 'GROUPS' | 'TIMES' | 'CANCELLATIONS',
  filters: OperatingRoomFilterDTO,
  options: { groupBy?: OperatingRoomGroupBy; measure?: OperatingRoomHeatMeasure; granularity?: OperatingRoomGranularity },
  format: OperatingRoomExportFormat,
): Promise<AxiosResponse<Blob>> {
  const body: OperatingRoomCriteriaDTO = { filters, scope, format }
  if (scope === 'GROUPS') body.groupBy = options.groupBy
  if (scope === 'HEATMAP') body.measure = options.measure
  if (scope === 'EVOLUTION') body.granularity = options.granularity
  return axiosInstance.post<Blob>(`${OPERATING_ROOM_API}/export`, body, { responseType: 'blob' })
}

/**
 * Exporta la lista completa con lo que se ve: los filtros de pantalla, los de columna, el buscador, el orden, el estado y la
 * celda del mapa (`params` es lo último que pidió la tabla; no se pagina).
 */
export function exportOperatingRoomList(
  format: OperatingRoomExportFormat,
  params: OperatingRoomTableParams,
): Promise<AxiosResponse<Blob>> {
  const body: OperatingRoomCriteriaDTO = { ...listCriteria(null, null, params), scope: 'LIST', format }
  return axiosInstance.post<Blob>(`${OPERATING_ROOM_API}/export`, body, { responseType: 'blob' })
}
